import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from 'axios';
import { isValidToken, setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  error: ''
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  PROFILE_SETTING: (state, action) => ({
    ...state,
    isAuthenticated: true
  }),
  RESET: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  SET_ERROR: (state, action) => {
    const { msg } = action.payload;

    return {
      ...state,
      error: msg
    };
  }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  updateProfile: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      const accessToken = window.localStorage.getItem('accessToken');
      const userDetails = window.localStorage.getItem('userDetails');

      if (accessToken && isValidToken(accessToken) && userDetails) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: JSON.parse(userDetails)
          }
        });
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('http://smartcollect-api.cp360apps.com/user/login/', {
      email,
      password
    });

    const { accessToken, user } = response?.data;

    setSession(accessToken, user);

    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const updateUserDetails = async () => {
    const accessToken = window.localStorage.getItem('accessToken');
    const userDetails = window.localStorage.getItem('userDetails');

    if (accessToken && isValidToken(accessToken) && userDetails) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: true,
          user: JSON.parse(userDetails)
        }
      });
    } else {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null
        }
      });
    }
  };

  const resetPassword = async (email) => {
    const response = await axios.post(
      'http://smartcollect-api.cp360apps.com/cp360adminlogin/send-reset-password-email/',
      {
        email
      }
    );

    const { accessToken } = response?.data;

    setSession(accessToken);
    dispatch({
      type: 'RESET',
      payload: {
        user: null
      }
    });
  };

  const changePassword = async (password, token) => {
    const response = await axios.post(
      `http://smartcollect-api.cp360apps.com/cp360adminlogin/reset-password/${token}/`,
      {
        password,
        password2: password
      }
    );
  };

  const updateProfile = async (password) => {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      method: 'post',
      url: 'http://smartcollect-api.cp360apps.com/cp360adminlogin/auth-user-reset-password/',
      data: password
    });
    dispatch({ type: 'PROFILE_SETTING' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        resetPassword,
        changePassword,
        updateProfile,
        updateUserDetails
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
